<template>
  <div class="finance-container">
    <!-- 交易列表 -->
    <div class="table-wrapper">
      <div class="table-header">
        <div class="header-cell type">{{ $t('_jyjl._lx') }}</div>
        <div class="header-cell amount">{{ $t('_jyjl._je') }}</div>
        <div class="header-cell balance">{{ $t('_jyjl._ye') }}</div>
        <div class="header-cell frozen">{{ $t('_jyjl._dj') }}</div>
        <div class="header-cell video">{{ $t('_sp._dbye') }}</div>
        <div class="header-cell time">{{ $t('_jyjl._sj') }}</div>
        <div class="header-cell actions">{{ $t('_jyjl._cz') }}</div>
      </div>

      <!-- 列表项 -->
      <div
        v-for="item in transactions"
        :key="item.id"
        class="table-row"
      >
        <div class="row-cell type">
          <div class="type-info">
            <span class="type-name">{{ getTypeDesc(item.type) }}</span>
          </div>
        </div>

        <div class="row-cell amount">
          <span class="amount-value" :class="item.classify == 1 ? 'expense' : 'income'">
            {{ item.classify == 1 ? '-' : '+' }}${{ item.number }}
          </span>
        </div>

        <div class="row-cell balance">
          <span class="balance-value">${{ item.balance }}</span>
        </div>

        <div class="row-cell frozen">
          <span class="frozen-value" :class="{ 'has-frozen': item.freeze > 0 }">
            ${{ item.freeze }}
          </span>
        </div>

        <div class="row-cell video">
          <span class="video-value" :class="{ 'has-video': item.video > 0 }">
            ${{ item.video }}
          </span>
        </div>

        <div class="row-cell time">
          <div class="time-info">
            <span class="time-date">{{ formatDate(item.create_time) }}</span>
            <span class="time-hour">{{ formatTime(item.create_time) }}</span>
          </div>
        </div>

        <div class="row-cell actions">
          <button class="view-btn" @click="showDetail(item)">
            <EyeOutlined />
          </button>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="transactions.length === 0 && !loading" class="empty-state">
        <i class="fas fa-receipt"></i>
        <p>{{ $t('_jyjl._zwjyjl') }}</p>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>{{ $t('_a._jzz') }}</p>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-bar" v-if="transactions.length > 0">
      <button class="page-btn" :disabled="page === 1" @click="onPage(page - 1)">
        <LeftOutlined />
      </button>
      <span class="page-info">{{ $t('_jyjl._dny', { a: page, b: totalPages }) }}</span>
      <button class="page-btn" :disabled="page === totalPages" @click="onPage(page + 1)">
        <RightOutlined />
      </button>
    </div>

    <!-- 详情弹窗 -->
    <div v-if="selectedItem" class="modal-overlay" @click.self="selectedItem = null">
      <div class="modal-content">
        <div class="modal-header">
          <span class="modal-title">{{ $t('_jyjl._jyxq') }}</span>
          <button class="modal-close" @click="selectedItem = null">
            <CloseCircleOutlined />
          </button>
        </div>
        <div class="modal-body">
          <div class="detail-item">
            <span class="detail-label">{{ $t('_jyjl._jylx') }}</span>
            <span class="detail-value">{{ getTypeDesc(selectedItem.type) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ $t('_jyjl._jyje') }}</span>
            <span class="detail-value" :class="selectedItem.classify == 1 ? 'expense' : 'income'">
              {{ selectedItem.classify == 1 ? '-' : '+' }}${{ selectedItem.number }}
            </span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ $t('_jyjl._jyhye') }}</span>
            <span class="detail-value">${{ selectedItem.balance }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ $t('_jyjl._djye') }}</span>
            <span class="detail-value">${{ selectedItem.freeze }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ $t('_sp._dbye') }}</span>
            <span class="detail-value">${{ selectedItem.video }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ $t('_jyjl._jysj') }}</span>
            <span class="detail-value">{{ formatFullTime(selectedItem.create_time) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ $t('_jyjl._jyzt') }}</span>
            <span class="status-badge completed">{{ $t('_jyjl._ywc') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { EyeOutlined, RightOutlined, LeftOutlined, CloseCircleOutlined } from '@ant-design/icons-vue'

export default {
  name: 'FinanceRecords',
  components: {
    EyeOutlined,
    RightOutlined,
    LeftOutlined,
    CloseCircleOutlined
  },
  data() {
    return {
      selectedItem: null,
      transactions: [],
      page: 1,
      pageSize: 10,
      total: 0,
      loading: false
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    }
  },
  created() {
    this.getList()
  },
  methods: {
    getList() {
      this.loading = true
      const hide = this.$message.loading({ content: 'loading...', key: 'msg' })

      this.$api.userWallet
        .getWalletLogList({
          page: this.page,
          size: this.pageSize,
          search: ''
        })
        .then((res) => {
          hide()
          this.transactions = res.data?.data || []
          this.total = res.data?.total || 0
        })
        .catch(() => {
          hide()
          this.transactions = []
          this.total = 0
        })
        .finally(() => {
          this.loading = false
        })
    },

    onPage(page) {
      if (page < 1 || page > this.totalPages) return
      this.page = page
      this.getList()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    getTypeDesc(type) {
      const map = {
        1: this.$t('_jyjl._tp1'),
        2: this.$t('_jyjl._tp2'),
        3: this.$t('_jyjl._tp3'),
        4: this.$t('_jyjl._tp4'),
        5: this.$t('_jyjl._tp5'),
        6: this.$t('_jyjl._tp6'),
        7: this.$t('_jyjl._tp7'),
        8: this.$t('_jyjl._tp8'),
        9: this.$t('_jyjl._tp9'),
        10: this.$t('_jyjl._tp10'),
        11: this.$t('_jyjl._tp11')
      }
      return map[type] || type
    },

    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('zh-CN')
    },

    formatTime(date) {
      if (!date) return ''
      return new Date(date).toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatFullTime(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('zh-CN')
    },

    showDetail(item) {
      this.selectedItem = item
    }
  }
}
</script>

<style scoped>
/* ============================================================
   资金流水 - 暗色主题
   ============================================================ */

.finance-container {
  background: transparent;
  color: #e8edf2;
}

/* ============================================================
   表格
   ============================================================ */
.table-wrapper {
  background: #0f1419;
  border-radius: 14px;
  border: 1px solid #1a1f26;
  overflow: hidden;
}

/* 表头 */
.table-header {
  display: flex;
  padding: 12px 20px;
  background: #0a0d0f;
  border-bottom: 1px solid #1a1f26;
}

.header-cell {
  color: #5a6672;
  font-size: 15px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0 6px;
}

.header-cell.type { flex: 1.2; }
.header-cell.amount { flex: 1; }
.header-cell.balance { flex: 1; }
.header-cell.frozen { flex: 0.9; }
.header-cell.video { flex: 0.9; }
.header-cell.time { flex: 1.2; }
.header-cell.actions { flex: 0.6; text-align: center; }

/* 行 */
.table-row {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #141a20;
  transition: background 0.2s;
}

.table-row:hover {
  background: rgba(255, 255, 255, 0.02);
}

.table-row:last-child {
  border-bottom: none;
}

.row-cell {
  display: flex;
  align-items: center;
  padding: 0 6px;
  min-height: 44px;
}

.row-cell.type { flex: 1.2; }
.row-cell.amount { flex: 1; }
.row-cell.balance { flex: 1; }
.row-cell.frozen { flex: 0.9; }
.row-cell.video { flex: 0.9; }
.row-cell.time { flex: 1.2; }
.row-cell.actions { flex: 0.6; justify-content: center; }

/* ============================================================
   类型
   ============================================================ */
.type-name {
  color: #e8edf2;
  font-size: 14px;
  font-weight: 500;
}

/* ============================================================
   金额
   ============================================================ */
.amount-value {
  font-size: 15px;
  font-weight: 600;
}

.amount-value.income {
  color: #26a69a;
}

.amount-value.expense {
  color: #ef5350;
}

/* ============================================================
   余额
   ============================================================ */
.balance-value {
  color: #e8edf2;
  font-size: 14px;
  font-weight: 500;
}

/* ============================================================
   冻结金额
   ============================================================ */
.frozen-value {
  color: #5a6672;
  font-size: 14px;
}

.frozen-value.has-frozen {
  color: #ffb300;
}

/* ============================================================
   视频余额
   ============================================================ */
.video-value {
  color: #5a6672;
  font-size: 14px;
}

.video-value.has-video {
  color: #ffc107;
}

/* ============================================================
   时间
   ============================================================ */
.time-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.time-date {
  color: #e8edf2;
  font-size: 13px;
}

.time-hour {
  color: #5a6672;
  font-size: 12px;
}

/* ============================================================
   操作按钮
   ============================================================ */
.view-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #5a6672;
  cursor: pointer;
  transition: all 0.25s;
  font-size: 16px;
}

.view-btn:hover {
  background: rgba(229, 9, 20, 0.12);
  color: #e50914;
}

/* ============================================================
   空状态
   ============================================================ */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #5a6672;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.4;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}

/* ============================================================
   加载状态
   ============================================================ */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #5a6672;
}

.loading-state i {
  font-size: 32px;
  margin-bottom: 10px;
  color: #e50914;
}

.loading-state p {
  font-size: 14px;
  margin: 0;
}

/* ============================================================
   分页
   ============================================================ */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px 0 0;
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #1a1f26;
  border-radius: 8px;
  background: #0f1419;
  color: #8a949f;
  cursor: pointer;
  transition: all 0.25s;
}

.page-btn:hover:not(:disabled) {
  background: #181e24;
  border-color: #2a3138;
  color: #e8edf2;
}

.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-info {
  color: #8a949f;
  font-size: 14px;
}

/* ============================================================
   详情弹窗
   ============================================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.25s ease;
}

.modal-content {
  width: 420px;
  max-width: 92vw;
  background: #141a20;
  border-radius: 16px;
  border: 1px solid #1e262e;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
  animation: slideUp 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid #1a1f26;
}

.modal-title {
  color: #e8edf2;
  font-size: 18px;
  font-weight: 600;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #5a6672;
  cursor: pointer;
  font-size: 20px;
  transition: all 0.25s;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #e8edf2;
  transform: rotate(90deg);
}

.modal-body {
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  color: #5a6672;
  font-size: 14px;
}

.detail-value {
  color: #e8edf2;
  font-size: 14px;
  font-weight: 500;
}

.detail-value.income {
  color: #26a69a;
}

.detail-value.expense {
  color: #ef5350;
}

.status-badge {
  padding: 2px 14px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.completed {
  background: rgba(38, 166, 154, 0.15);
  color: #26a69a;
}

/* ============================================================
   动画
   ============================================================ */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ============================================================
   响应式
   ============================================================ */
@media (max-width: 820px) {
  .table-header {
    display: none;
  }

  .table-row {
    flex-direction: column;
    align-items: stretch;
    padding: 14px 16px;
    gap: 6px;
  }

  .row-cell {
    padding: 4px 0;
    min-height: auto;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  }

  .row-cell::before {
    content: attr(data-label);
    color: #5a6672;
    font-size: 13px;
    font-weight: 500;
  }

  .row-cell:last-child {
    border-bottom: none;
    justify-content: center;
    padding-top: 4px;
  }

  /* 设置 data-label */
  .row-cell.type::before { content: '类型'; }
  .row-cell.amount::before { content: '金额'; }
  .row-cell.balance::before { content: '余额'; }
  .row-cell.frozen::before { content: '冻结'; }
  .row-cell.video::before { content: '视频余额'; }
  .row-cell.time::before { content: '时间'; }
  .row-cell.actions::before { content: ''; }

  .row-cell.type { flex: none; }
  .row-cell.amount { flex: none; }
  .row-cell.balance { flex: none; }
  .row-cell.frozen { flex: none; }
  .row-cell.video { flex: none; }
  .row-cell.time { flex: none; }
  .row-cell.actions { flex: none; }

  .time-info {
    align-items: flex-end;
  }

  .time-date {
    font-size: 13px;
  }

  .modal-content {
    width: 95vw;
  }

  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}

@media (max-width: 480px) {
  .table-row {
    padding: 12px 14px;
    gap: 4px;
  }

  .row-cell {
    font-size: 13px;
    padding: 3px 0;
  }

  .row-cell::before {
    font-size: 12px;
  }

  .type-name {
    font-size: 13px;
  }

  .amount-value {
    font-size: 14px;
  }

  .balance-value,
  .frozen-value,
  .video-value {
    font-size: 13px;
  }

  .time-date {
    font-size: 12px;
  }

  .time-hour {
    font-size: 11px;
  }

  .page-btn {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }

  .page-info {
    font-size: 13px;
  }
}
</style>